/**
 * Repositorio.
 *
 * Toda acción del usuario pasa por acá y hace siempre lo mismo, en este
 * orden: escribe en IndexedDB dentro de una transacción, encola la operación
 * en el outbox, y devuelve. Nunca toca la red. Eso es lo que hace que la
 * interfaz responda igual de rápido con wifi que sin él, y lo que permite
 * probar todo el flujo del restaurante sin backend levantado.
 *
 * La escritura local y el encolado van en la MISMA transacción a propósito:
 * si el navegador se cierra en el peor momento posible, no puede quedar un
 * ítem cobrado que nadie va a sincronizar, ni una operación en cola que
 * apunte a un ítem que no existe.
 */

import {
  db,
  leerMeta,
  type ItemLocal,
  type MesaLocal,
  type OperacionOutbox,
  type OrdenLocal,
  type PagoLocal,
} from './db';
import { uuidv7 } from '../lib/uuid';
import type { MetodoPago, Producto, PuestoNuevo } from '../tipos/contrato';

/**
 * Propina sugerida. En Colombia es voluntaria y el cliente puede pedir que se
 * retire, por eso es un toggle y no un cargo fijo. El backend la recalcula con
 * la tarifa configurada del restaurante; este valor es solo para mostrarla al
 * instante, sin esperar respuesta del servidor.
 */
export const TARIFA_PROPINA = 0.1;

/* ─────────────────────────────── Totales ──────────────────────────────── */

export interface Totales {
  subtotal: number;
  propina: number;
  total: number;
  cantidadItems: number;
}

export function calcularTotales(items: ItemLocal[], propinaAceptada: boolean): Totales {
  const vivos = items.filter((i) => !i.anulado);
  const subtotal = vivos.reduce((s, i) => s + i.precioUnitario * i.cantidad, 0);
  const propina = propinaAceptada ? Math.round(subtotal * TARIFA_PROPINA) : 0;
  return {
    subtotal,
    propina,
    total: subtotal + propina,
    cantidadItems: vivos.reduce((s, i) => s + i.cantidad, 0),
  };
}

/* ──────────────────────────────── Outbox ──────────────────────────────── */

function nuevaOperacion(
  tipo: OperacionOutbox['tipo'],
  ordenId: string,
  carga: Record<string, unknown>,
  bloqueada = false,
): OperacionOutbox {
  return {
    tipo,
    ordenId,
    carga,
    creadaEn: Date.now(),
    intentos: 0,
    proximoIntentoEn: 0,
    ultimoError: null,
    bloqueada,
  };
}

/* ──────────────────────────────── Órdenes ─────────────────────────────── */

/** Consecutivo local. Provisional: al sincronizar manda el número del servidor. */
async function siguienteNumeroLocal(): Promise<string> {
  const clave = 'consecutivoLocal';
  const fila = await db.meta.get(clave);
  const siguiente = ((fila?.valor as number | undefined) ?? 0) + 1;
  await db.meta.put({ clave, valor: siguiente });

  const prefijo = ((await db.meta.get('prefijoDispositivo'))?.valor as string | undefined) ?? 'A';
  return `${prefijo}-${String(siguiente).padStart(4, '0')}`;
}

interface DatosCuenta {
  comensales: number;
  mesero: string;
  dispositivoId: string;
  etiqueta?: string | null;
}

function nuevaOrden(
  mesaId: string | null,
  mesaNumero: string | null,
  numero: string,
  datos: DatosCuenta,
): OrdenLocal {
  return {
    id: uuidv7(),
    numero,
    mesaId,
    mesaNumero,
    etiqueta: datos.etiqueta?.trim() || null,
    comensales: datos.comensales,
    tipo: 'mesa',
    estado: 'abierta',
    propinaAceptada: true,
    notas: null,
    meseroNombre: datos.mesero,
    abiertaEn: Date.now(),
    version: 0,
    sincronizada: false,
  };
}

/**
 * Abrir una cuenta en un puesto existente.
 *
 * Ya **no** hay una cuenta por mesa: si dos grupos comparten mesa, se abren
 * dos cuentas y cada una se cobra por separado. Lo que las distingue es la
 * etiqueta, no el número del puesto — este restaurante no tiene las mesas
 * numeradas.
 */
export async function abrirCuenta(
  mesaId: string,
  mesaNumero: string,
  datos: DatosCuenta,
): Promise<OrdenLocal> {
  const orden = nuevaOrden(mesaId, mesaNumero, await siguienteNumeroLocal(), datos);

  await db.transaction('rw', [db.ordenes, db.outbox], async () => {
    await db.ordenes.add(orden);
    await db.outbox.add(
      nuevaOperacion('orden.abrir', orden.id, {
        id: orden.id,
        mesaId,
        etiqueta: orden.etiqueta,
        comensales: orden.comensales,
        tipo: 'mesa',
        dispositivoId: datos.dispositivoId,
        origenOffline: !navigator.onLine,
      }),
    );
  });

  return orden;
}

/**
 * Abrir una cuenta creando el puesto en el mismo paso: el caso de la barra.
 *
 * El puesto se crea local con un ID de esta tablet y se manda `puestoNuevo` en
 * vez de `mesaId`. El servidor lo crea, le asigna su número real (B1, B2…) y
 * devuelve la orden ya asociada; ahí `sync.ts` reemplaza el puesto local por
 * el definitivo. Así el mesero puede sentar a alguien en la barra sin señal y
 * sin pasar por ninguna pantalla de configuración.
 */
export async function abrirPuestoNuevo(
  puesto: PuestoNuevo & { nombre: string },
  datos: DatosCuenta,
): Promise<OrdenLocal> {
  const esTemporal = puesto.esTemporal ?? true;

  const mesa: MesaLocal = {
    id: uuidv7(),
    // Provisional: el número definitivo lo asigna el backend al sincronizar.
    numero: esTemporal ? 'B·' : '··',
    nombre: puesto.nombre,
    zona: puesto.zona ?? null,
    capacidad: puesto.capacidad ?? 2,
    esTemporal,
    estadoRemoto: null,
    local: true,
  };

  const orden = nuevaOrden(mesa.id, mesa.numero, await siguienteNumeroLocal(), datos);

  await db.transaction('rw', [db.mesasRemotas, db.ordenes, db.outbox], async () => {
    await db.mesasRemotas.add(mesa);
    await db.ordenes.add(orden);
    await db.outbox.add(
      nuevaOperacion('orden.abrir', orden.id, {
        id: orden.id,
        puestoNuevo: {
          nombre: puesto.nombre,
          ...(puesto.zona ? { zona: puesto.zona } : {}),
          capacidad: mesa.capacidad,
          esTemporal,
        },
        mesaLocalId: mesa.id,
        etiqueta: orden.etiqueta,
        comensales: orden.comensales,
        tipo: 'mesa',
        dispositivoId: datos.dispositivoId,
        origenOffline: !navigator.onLine,
      }),
    );
  });

  return orden;
}

/** Renombrar la cuenta o corregir comensales. Viaja como PATCH /ordenes/:id. */
export async function renombrarCuenta(
  ordenId: string,
  datos: { etiqueta: string | null; comensales: number },
): Promise<void> {
  const etiqueta = datos.etiqueta?.trim() || null;

  await db.transaction('rw', [db.ordenes, db.outbox], async () => {
    await db.ordenes.update(ordenId, { etiqueta, comensales: datos.comensales });
    await db.outbox.add(
      nuevaOperacion('orden.renombrar', ordenId, { etiqueta, comensales: datos.comensales }),
    );
  });
}

/**
 * Mover una cuenta a otro puesto. Sin esto, la única salida cuando los
 * clientes se cambian de mesa sería cobrar y volver a tomar todo el pedido.
 */
export async function moverCuenta(
  ordenId: string,
  destino: { id: string; numero: string },
): Promise<void> {
  await db.transaction('rw', [db.ordenes, db.outbox], async () => {
    await db.ordenes.update(ordenId, { mesaId: destino.id, mesaNumero: destino.numero });
    await db.outbox.add(nuevaOperacion('orden.mover', ordenId, { mesaDestinoId: destino.id }));
  });
}

/** El mesero a cargo es dato de sala: no viaja al backend, no afecta el total. */
export async function asignarMesero(ordenId: string, mesero: string): Promise<void> {
  await db.ordenes.update(ordenId, { meseroNombre: mesero });
}

export async function marcarPropina(ordenId: string, aceptada: boolean): Promise<void> {
  await db.transaction('rw', [db.ordenes, db.outbox], async () => {
    await db.ordenes.update(ordenId, { propinaAceptada: aceptada });
    await db.outbox.add(nuevaOperacion('orden.propina', ordenId, { aceptada }));
  });
}

export async function pedirCuenta(ordenId: string): Promise<void> {
  await db.transaction('rw', [db.ordenes, db.outbox], async () => {
    await db.ordenes.update(ordenId, { estado: 'cuenta_solicitada' });
    await db.outbox.add(nuevaOperacion('orden.cuenta', ordenId, {}));
  });
}

/* ───────────────────────────────── Ítems ──────────────────────────────── */

/**
 * Agregar producto. Si ya está en la orden, suma cantidad en vez de crear una
 * segunda línea — es lo que espera el mesero cuando toca dos veces el mismo
 * plato, y mantiene la cuenta legible.
 */
export async function agregarProducto(ordenId: string, producto: Producto): Promise<void> {
  const existente = await db.items
    .where('ordenId')
    .equals(ordenId)
    .filter((i) => !i.anulado && i.productoId === producto.id)
    .first();

  if (existente) {
    await cambiarCantidad(existente.id, 1);
    return;
  }

  const item: ItemLocal = {
    id: uuidv7(),
    ordenId,
    productoId: producto.id,
    nombreSnap: producto.nombre,
    precioUnitario: Number(producto.precio),
    cantidad: 1,
    notas: null,
    estado: producto.requiereCocina ? 'pendiente' : 'listo',
    creadoEn: Date.now(),
    anulado: false,
  };

  await db.transaction('rw', [db.items, db.outbox], async () => {
    await db.items.add(item);
    await db.outbox.add(
      nuevaOperacion('orden.item.agregar', ordenId, {
        id: item.id,
        productoId: producto.id,
        cantidad: 1,
      }),
    );
  });
}

/** Suma o resta unidades. Llegar a cero anula el ítem, no lo borra. */
export async function cambiarCantidad(itemId: string, delta: number): Promise<void> {
  const item = await db.items.get(itemId);
  if (!item || item.anulado) return;

  const nueva = item.cantidad + delta;
  if (nueva <= 0) {
    await anularItem(itemId, 'Retirado antes de cobrar');
    return;
  }

  await db.transaction('rw', [db.items, db.outbox], async () => {
    await db.items.update(itemId, { cantidad: nueva });
    await db.outbox.add(
      nuevaOperacion('orden.item.actualizar', item.ordenId, { itemId, cantidad: nueva }),
    );
  });
}

/**
 * Anular, no borrar. Un plato que ya fue a cocina y se anula es información
 * contable: el backend lo guarda con motivo y el trigger descuenta el
 * subtotal. Borrarlo dejaría un hueco imposible de auditar.
 */
export async function anularItem(itemId: string, motivo: string): Promise<void> {
  const item = await db.items.get(itemId);
  if (!item) return;

  await db.transaction('rw', [db.items, db.outbox], async () => {
    await db.items.update(itemId, { anulado: true, estado: 'anulado' });
    await db.outbox.add(
      nuevaOperacion('orden.item.anular', item.ordenId, { itemId, motivo }),
    );
  });
}

/* ───────────────────────── Descartar cuenta ───────────────────────────── */

/**
 * Deshace una cuenta abierta por error.
 *
 * Distinta de anular: una cuenta vacía **nunca fue una venta**. No hubo
 * producto ni plata, así que no hay nada que auditar y dejarla como 'anulada'
 * solo ensuciaría el histórico y el contador de anuladas del cierre de caja.
 *
 * El detalle fino está en el outbox. Si la cuenta todavía no llegó al
 * servidor —lo normal, porque se descarta a los segundos de abrirla— alcanza
 * con borrar su `orden.abrir` de la cola: para el backend esa orden nunca
 * existió y no hace falta mandarle nada. Solo si ya se sincronizó se encola
 * el DELETE.
 *
 * Devuelve `false` si la cuenta ya tiene productos: eso se anula con motivo.
 */
export async function descartarCuenta(ordenId: string): Promise<boolean> {
  const orden = await db.ordenes.get(ordenId);
  if (!orden) return false;

  const items = await db.items.where('ordenId').equals(ordenId).toArray();
  if (items.some((i) => !i.anulado)) return false;

  const pagos = await db.pagos.where('ordenId').equals(ordenId).toArray();
  if (pagos.some((p) => !p.anuladoEn)) return false;

  await db.transaction(
    'rw',
    [db.ordenes, db.items, db.outbox, db.mesasRemotas],
    async () => {
      const pendientesDeEsta = await db.outbox.where('ordenId').equals(ordenId).toArray();
      const nuncaSubio = pendientesDeEsta.some((o) => o.tipo === 'orden.abrir');

      // Se limpian TODAS las operaciones de esta cuenta: mandar un cambio de
      // propina de una orden que se está borrando solo produciría un 404.
      await db.outbox.where('ordenId').equals(ordenId).delete();

      if (!nuncaSubio && orden.sincronizada) {
        await db.outbox.add(nuevaOperacion('orden.descartar', ordenId, {}));
      }

      await db.items.where('ordenId').equals(ordenId).delete();
      await db.ordenes.delete(ordenId);

      // Puesto temporal creado junto con esta cuenta y que queda sin ninguna:
      // desaparece de la grilla, igual que hace el trigger del backend.
      if (orden.mesaId) {
        const mesa = await db.mesasRemotas.get(orden.mesaId);
        const quedan = await db.ordenes.where('mesaId').equals(orden.mesaId).count();
        if (mesa?.esTemporal && quedan === 0 && mesa.local) {
          await db.mesasRemotas.delete(orden.mesaId);
        }
      }
    },
  );

  return true;
}

/* ──────────────────────────────── Cobro ───────────────────────────────── */

export interface DatosCobro {
  /** Ya traducido al valor que acepta la base: 'nequi', 'tarjeta_debito', … */
  metodo: MetodoPago;
  /** Etiqueta legible para el recibo. */
  detalle: string;
  referencia: string | null;
  ultimosDigitos: string | null;
  recibido: number;
}

/**
 * Cierra la orden y libera la mesa.
 *
 * El pago se guarda con un snapshot completo de los ítems porque el recibo
 * tiene que poder reimprimirse dentro de dos años, cuando el producto quizá
 * ya no exista en el catálogo o tenga otro precio.
 *
 * El cobro se cierra localmente sin esperar al servidor, igual que todo lo
 * demás. Lo que viaja después es la intención de cobro con su UUID: el
 * backend es idempotente por `id`, así que si el ACK se pierde y el outbox
 * reintenta, responde `duplicado: true` en vez de cobrar dos veces.
 */
export async function cobrar(ordenId: string, datos: DatosCobro): Promise<PagoLocal> {
  const orden = await db.ordenes.get(ordenId);
  if (!orden) throw new Error('La orden ya no existe');

  const items = await db.items.where('ordenId').equals(ordenId).toArray();
  const totales = calcularTotales(items, orden.propinaAceptada);

  // El turno sale de la copia local que dejó el último pull. Sin él, el
  // resumen de caja no puede agrupar los cobros y mostraría siempre cero.
  const turno = await leerMeta<{ turno: { id: string } | null } | null>('turnoActual', null);
  const turnoId = turno?.turno?.id ?? '';

  const pago: PagoLocal = {
    id: uuidv7(),
    ordenId,
    turnoId,
    metodo: datos.metodo,
    detalle: datos.detalle,
    referencia: datos.referencia || null,
    subtotal: totales.subtotal,
    propina: totales.propina,
    total: totales.total,
    // `monto` es lo cobrado en ESTE movimiento; hoy siempre el total, pero
    // con pagos mixtos será una fracción. El resumen suma `monto`, no `total`,
    // para no contar dos veces una cuenta pagada en dos partes.
    monto: totales.total,
    recibido: datos.metodo === 'efectivo' ? datos.recibido : totales.total,
    cambio: datos.metodo === 'efectivo' ? datos.recibido - totales.total : 0,
    sincronizado: false,
    anuladoEn: null,
    creadoEn: Date.now(),
    itemsSnap: items
      .filter((i) => !i.anulado)
      .map((i) => ({ nombre: i.nombreSnap, cantidad: i.cantidad, precio: i.precioUnitario })),
    mesaNumero: orden.mesaNumero,
    ordenNumero: orden.numero,
    meseroNombre: orden.meseroNombre,
    minutos: Math.max(0, Math.floor((Date.now() - orden.abiertaEn) / 60000)),
  };

  await db.transaction('rw', [db.ordenes, db.pagos, db.outbox], async () => {
    const actual = await db.ordenes.get(ordenId);
    if (!actual) throw new Error(`La orden ${ordenId} no existe localmente`);
    if (actual.estado === 'pagada') return;
    
    await db.pagos.add(pago);
    await db.ordenes.update(ordenId, { estado: 'pagada' });
    await db.outbox.add(
      nuevaOperacion('orden.pago', ordenId, {
        id: pago.id,
        metodo: pago.metodo,
        // `recibido` solo aplica a efectivo; el backend rechaza con 422 si
        // llega en tarjeta. `monto` se omite adrede: manda el saldo del
        // servidor (ver comentario en api/endpoints.ts).
        ...(pago.metodo === 'efectivo' ? { recibido: pago.recibido } : {}),
        ...(pago.referencia ? { referencia: pago.referencia } : {}),
        ...(datos.ultimosDigitos ? { ultimosDigitos: datos.ultimosDigitos } : {}),
      }),
    );
  });

  return pago;
}

/* ───────────────────────────── Consultas ──────────────────────────────── */

/** Órdenes vivas, para pintar la grilla de mesas. */
export const ordenesAbiertas = () =>
  db.ordenes.filter((o) => o.estado === 'abierta' || o.estado === 'cuenta_solicitada').toArray();

export const itemsDe = (ordenId: string) =>
  db.items.where('ordenId').equals(ordenId).sortBy('creadoEn');

export const ultimosCobros = (limite = 12) =>
  db.pagos.orderBy('creadoEn').reverse().limit(limite).toArray();
